import React, { useState, useCallback, useMemo } from "react";
import { Stack, Snackbar, Alert } from "@mui/material";
import { useAccount } from "../../context/AccountContext";
import { useDevCustomer } from "./hooks/useDevCustomer";
import { useDevSnackbar } from "./hooks/useDevSnackbar";
import { useRechargeApi } from "./hooks/useRechargeApi";
import { remapMealsToPackSize, findHiddenPriceProduct, getPackPriceFromHidden, getBundleVariantId } from "./helpers";
import type { SubscriptionData, ModalType, PaymentMethod, DogProfileExtended } from "./types";

// Sections
import { CustomerSearchCard } from "./sections/CustomerSearchCard";
import { CustomerHeader } from "./sections/CustomerHeader";
import { DataIssuesSection } from "./sections/DataIssuesSection";
import { SubscriptionsSection } from "./sections/SubscriptionsSection";
import { OnetimesSection } from "./sections/OnetimesSection";
import { UpcomingDeliveriesSection } from "./sections/UpcomingDeliveriesSection";
import { OrderHistorySection } from "./sections/OrderHistorySection";
import { DogProfilesSection } from "./sections/DogProfilesSection";
import { PaymentMethodsSection } from "./sections/PaymentMethodsSection";

// Modals
import { EditMealsModal } from "./modals/EditMealsModal";
import { ChangePlanModal } from "./modals/ChangePlanModal";
import { ChangeDeliveryModal } from "./modals/ChangeDeliveryModal";
import { CancelModal } from "./modals/CancelModal";
import { ResumeModal } from "./modals/ResumeModal";
import { SkipDeliveryModal } from "./modals/SkipDeliveryModal";
import { ChangeDailyServingModal } from "./modals/ChangeDailyServingModal";
import { AddTreatsModal } from "./modals/AddTreatsModal";
import { MoveSubscriptionModal } from "./modals/MoveSubscriptionModal";
import { DeletePaymentModal } from "./modals/DeletePaymentModal";
import { PaymentUpdateModal } from "./modals/PaymentUpdateModal";
import { MergeAddressesModal } from "./modals/MergeAddressesModal";
import { OnetimeModal } from "./modals/OnetimeModal";
import { EditAddressModal } from "./modals/EditAddressModal";
import { ChangeQuantityModal } from "./modals/ChangeQuantityModal";
import { EditTreatModal } from "./modals/EditTreatModal";
import { DeleteDogModal } from "./modals/DeleteDogModal";
import { EditDogModal } from "./modals/EditDogModal";

export function DevPage() {
  const { products, dogs: accountDogs } = useAccount();
  const customerState = useDevCustomer();
  const snackbar = useDevSnackbar();
  const { call } = useRechargeApi();

  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedSub, setSelectedSub] = useState<SubscriptionData | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod | null>(null);
  const [selectedDog, setSelectedDog] = useState<DogProfileExtended | null>(null);

  const {
    customer, subscriptions, addresses, paymentMethods, upcomingCharges, loading, error,
    searchByEmail, searchById, refreshData,
    addressGroups, dogProfiles, allSubsIssues, recipeProducts, getVariantInfo,
  } = customerState;

  const mergedDogProfiles = useMemo((): DogProfileExtended[] => {
    const map = new Map<string, DogProfileExtended>();
    for (const d of accountDogs) {
      map.set(d.id, {
        id: d.id,
        name: d.name,
        breed: d.breed,
        weightKg: d.weightKg,
        dailyGrams: d.dailyGrams,
        selectedPackSize: d.selectedPackSize,
        selectedPlanDays: d.selectedPlanDays,
        source: "account",
        deleted: (d as any).deleted,
      });
    }
    for (const dp of dogProfiles) {
      if (!map.has(dp.id)) {
        map.set(dp.id, { ...dp, source: "subscription" });
      }
    }
    return Array.from(map.values());
  }, [accountDogs, dogProfiles]);

  const activeDogCount = useMemo(() => {
    return mergedDogProfiles.filter(d => d.source === "account" && !d.deleted).length;
  }, [mergedDogProfiles]);

  const openModal = useCallback((type: ModalType, subOrPm?: any, addressId?: string) => {
    setActiveModal(type);
    if (type === "deletePayment" || type === "paymentUpdate") {
      setSelectedPayment(subOrPm || null);
    } else if (type === "deleteDog" || type === "editDog") {
      setSelectedDog(subOrPm || null);
    } else {
      setSelectedSub(subOrPm || null);
    }
    setSelectedAddressId(addressId || null);
  }, []);

  const openDeliveryModal = useCallback((addressId: string) => {
    const group = addressGroups.find(g => g.addressId === addressId);
    if (!group || group.allSubs.length === 0) return;
    setSelectedSub(group.allSubs[0]);
    setSelectedAddressId(addressId);
    setActiveModal("delivery");
  }, [addressGroups]);

  const handleEditAddress = useCallback((addressId: string) => {
    setSelectedAddressId(addressId);
    setActiveModal("editAddress");
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setSelectedSub(null);
    setSelectedAddressId(null);
    setSelectedPayment(null);
    setSelectedDog(null);
  }, []);

  const handleRestoreDog = useCallback(async (dog: DogProfileExtended) => {
    try {
      const res = await call("/account/dogs/restore", {
        method: "POST",
        body: { dogId: dog.id },
      });
      if (!res.ok) throw new Error(res.error || "Failed to restore dog");
      snackbar.show(`${dog.name || "Dog"} restored successfully!`);
      refreshData();
    } catch (err: any) {
      snackbar.show(err.message || "Failed to restore dog", "error");
    }
  }, [call, snackbar, refreshData]);

  const handleQuickFixMismatch = useCallback(async (sub: SubscriptionData, targetPackSize: number) => {
    const mealsJsonStr = sub.properties?.find(p => p.name === "_paws_meals_json")?.value;
    const currentMeals = mealsJsonStr ? JSON.parse(mealsJsonStr) : [];
    const planDaysStr = sub.properties?.find(p => p.name === "_paws_plan_days")?.value;
    const planDays = planDaysStr ? parseInt(planDaysStr, 10) : 14;

    if (!currentMeals || currentMeals.length === 0) throw new Error("No meals found");

    const remapped = remapMealsToPackSize(currentMeals, targetPackSize, products);
    if (remapped.length === 0) throw new Error("Could not remap meals");

    const hiddenProduct = findHiddenPriceProduct(products, planDays);
    const packPrice = getPackPriceFromHidden(hiddenProduct, targetPackSize);
    if (!packPrice || packPrice <= 0) throw new Error("Unable to calculate pricing");

    const totalMealCount = remapped.reduce((sum, m) => sum + m.count, 0);
    const newPrice = packPrice * totalMealCount;
    const newVariantId = getBundleVariantId(products, planDays, targetPackSize) || sub.properties?.find(p => p.name === "_paws_box_variant_id")?.value || String(sub.id);

    const mealsSummary = remapped.filter(m => m.count > 0).map(m => `${m.count}x ${m.productTitle} ${targetPackSize}g`).join(", ");

    const res = await call("/recharge/subscription/change-frequency", {
      method: "POST",
      body: { subscriptionId: sub.id, newPlanDays: planDays, newPackSizeGrams: targetPackSize, newVariantId, meals: remapped, newPrice, mealsSummary },
    });

    if (!res.ok) throw new Error(res.error || "Failed to fix mismatch");

    snackbar.show(`Fixed! Meals remapped to ${targetPackSize}g variants.`);
    refreshData();
  }, [products, call, refreshData, snackbar]);

  const selectedAddress = selectedAddressId
    ? addresses.find(a => String(a.id) === selectedAddressId) || null
    : null;

  return (
    <Stack spacing={{ xs: 3, md: 4 }}>
      <CustomerSearchCard
        onSearchByEmail={searchByEmail}
        onSearchById={searchById}
        loading={loading}
        error={error}
        successMessage={null}
        onDismissSuccess={() => {}}
      />

      {customer && (
        <>
          <CustomerHeader
            customer={customer}
            addressGroups={addressGroups}
            subscriptions={subscriptions}
            allSubsIssues={allSubsIssues}
          />

          {allSubsIssues.hasAnyIssues && (
            <DataIssuesSection
              subsWithIssues={allSubsIssues.subsWithIssues}
              onOpenModal={openModal}
              onQuickFixMismatch={handleQuickFixMismatch}
            />
          )}

          <SubscriptionsSection
            addressGroups={addressGroups}
            onOpenModal={openModal}
            onOpenDeliveryModal={openDeliveryModal}
            onEditAddress={handleEditAddress}
            getVariantInfo={getVariantInfo}
            getSubIssues={(subId) => allSubsIssues.issuesMap.get(subId)}
          />

          <OnetimesSection
            customerId={customer.id}
            addressGroups={addressGroups}
            onOpenModal={(type) => setActiveModal(type)}
            onSuccess={snackbar.show}
            onError={(msg) => snackbar.show(msg, "error")}
          />

          <UpcomingDeliveriesSection
            upcomingCharges={upcomingCharges}
            addresses={addresses}
            products={products}
            subscriptions={subscriptions}
            onOpenModal={openModal}
          />

          <OrderHistorySection
            customerId={customer.id}
            products={products}
            subscriptions={subscriptions}
          />

          <DogProfilesSection
            dogProfiles={mergedDogProfiles}
            onEditDog={(dog) => openModal("editDog", dog)}
            onDeleteDog={(dog) => openModal("deleteDog", dog)}
            onRestoreDog={handleRestoreDog}
          />

          <PaymentMethodsSection paymentMethods={paymentMethods} onOpenModal={openModal as any} />
        </>
      )}

      {/* Modals */}
      <EditMealsModal
        open={activeModal === "meals"} onClose={closeModal} sub={selectedSub}
        products={products} recipeProducts={recipeProducts} getVariantInfo={getVariantInfo}
        subIssues={selectedSub ? allSubsIssues.issuesMap.get(selectedSub.id) : undefined}
        onSuccess={(msg) => snackbar.show(msg)} onError={(msg) => snackbar.show(msg, "error")} onRefresh={refreshData}
      />
      <ChangePlanModal
        open={activeModal === "plan"} onClose={closeModal} sub={selectedSub}
        products={products} onSuccess={(msg) => snackbar.show(msg)} onError={(msg) => snackbar.show(msg, "error")} onRefresh={refreshData}
      />
      <ChangeDeliveryModal
        open={activeModal === "delivery"} onClose={closeModal} sub={selectedSub}
        addressId={selectedAddressId} addressGroups={addressGroups}
        onSuccess={(msg) => snackbar.show(msg)} onError={(msg) => snackbar.show(msg, "error")} onRefresh={refreshData}
      />
      <CancelModal open={activeModal === "cancel"} onClose={closeModal} sub={selectedSub} onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData} />
      <ResumeModal open={activeModal === "resume"} onClose={closeModal} sub={selectedSub} onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData} />
      <SkipDeliveryModal open={activeModal === "skip"} onClose={closeModal} sub={selectedSub} onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData} />
      <ChangeDailyServingModal open={activeModal === "dailyServing"} onClose={closeModal} sub={selectedSub} products={products} onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData} />
      <AddTreatsModal open={activeModal === "addTreats"} onClose={closeModal} sub={selectedSub} products={products} onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData} />
      <MoveSubscriptionModal open={activeModal === "moveSub"} onClose={closeModal} sub={selectedSub} addressGroups={addressGroups} onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData} />
      <DeletePaymentModal open={activeModal === "deletePayment"} onClose={closeModal} paymentMethod={selectedPayment} onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData} />
      <PaymentUpdateModal open={activeModal === "paymentUpdate"} onClose={closeModal} paymentMethod={selectedPayment} onSuccess={(msg) => snackbar.show(msg)} />
      <MergeAddressesModal open={activeModal === "mergeAddresses"} onClose={closeModal} addressGroups={addressGroups} onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData} />
      <OnetimeModal open={activeModal === "onetime"} onClose={closeModal} products={products} addressGroups={addressGroups} onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData} />
      <EditAddressModal
        open={activeModal === "editAddress"} onClose={closeModal}
        address={selectedAddress} addressId={selectedAddressId}
        onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData}
      />
      <ChangeQuantityModal
        open={activeModal === "changeQuantity"} onClose={closeModal}
        sub={selectedSub} onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData}
      />
      <EditTreatModal
        open={activeModal === "editTreat"} onClose={closeModal}
        sub={selectedSub} getVariantInfo={getVariantInfo}
        onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData}
      />
      <DeleteDogModal
        open={activeModal === "deleteDog"} onClose={closeModal}
        dog={selectedDog} activeDogCount={activeDogCount}
        onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData}
      />
      <EditDogModal
        open={activeModal === "editDog"} onClose={closeModal}
        dog={selectedDog} onSuccess={(msg) => snackbar.show(msg)} onRefresh={refreshData}
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={snackbar.hide} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={snackbar.hide} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
